import React, { useState, useEffect, useMemo } from 'react';
import { useFormik } from 'formik';
import { useClient } from 'jsonapi-react';
import toast from 'react-hot-toast';
import { useLocation, useNavigate } from 'react-router-dom';
import { FiChevronLeft } from 'react-icons/fi';
import { FilterSelectNative } from '@/components/custom/filter';
import { EmptyState } from '@/components/custom/empty-state';
import AccordionItem from './AccordionItem';
import { Loader } from '@/components/custom/loader';
import ChallengeHeader from './ChallengeHeader';
import ChallengeResultBanner from './ChallengeResultBanner';
import ChallengeTab from '../../ChallengeTab';
import { DashboardContent } from '@/layouts/dashboard';
import usePlayfulTeacher from '@/hooks/usePlayfulTeacher';
import { TiaBanner } from '@/components/custom/tia-banner';
import { JOINHA } from '@/pages/dashboard/home-teacher/teacher-character';
import ParticipantSummary from './Playful/ParticipantSummary';

export default function ViewParticipantResult(props) {
  const navigate = useNavigate();
  const { challengeId, selectedTab, profile, uri, studentId } = props;
  const { state } = useLocation();
  const isPlayfulTeacherTheme = usePlayfulTeacher();

  const client = useClient();

  const [loading, setLoading] = useState(false);
  const [challenges, setChallenges] = useState([]);
  const [studentChallenge, setStudentChallenge] = useState();
  const [param, setParam] = useState('period');
  const [orderByPeriod, setOrderByPeriod] = useState('descendent');
  const [orderByAccuracy, setOrderByAccuracy] = useState('descendent');

  const filters = useFormik({
    initialValues: {
      theme: ''
    }
  });

  const orderedChallenges = useMemo(() => {
    let newArr;

    if (param === 'period' && orderByPeriod === 'descendent') {
      newArr = [...challenges].sort((a, b) => {
        return a.challenge['initial-date'] - b.challenge['initial-date'];
      });
    }
    if (param === 'period' && orderByPeriod === 'ascendent') {
      newArr = [...challenges].sort((a, b) => {
        return b.challenge['initial-date)'] - a.challenge['initial-date'];
      });
    }
    if (param === 'accuracy' && orderByAccuracy === 'descendent') {
      newArr = challenges.sort((a, b) => a['student-accuracy'] - b['student-accuracy']);
    }
    if (param === 'accuracy' && orderByAccuracy === 'ascendent') {
      newArr = challenges.sort((a, b) => b['student-accuracy'] - a['student-accuracy']);
    }

    return newArr;
  }, [param, orderByPeriod, orderByAccuracy, challenges]);

  function createDate(dateString) {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  const fetchChallenges = async () => {
    setLoading(true);
    const { data, error } = await client.fetch(`/challenges/student_results/${studentId}/question_accuracy`);
    if (error) {
      toast.error('Erro ao buscar desafios');
    } else {
      setChallenges(
        data.data.map(item => {
          return { ...item, challenge: { ...item.challenge, 'initial-date': createDate(item.challenge['initial-date']), 'end-date': createDate(item.challenge['end-date']) } };
        })
      );
    }

    setLoading(false);
  };

  const fetchStudentChallenge = async () => {
    const { data, error } = await client.fetch(`/challenges/student_results/${studentId}`);
    if (error) {
      toast.error('Erro ao buscar desafios');
    } else {
      setStudentChallenge(data.data);
    }
  };

  useEffect(() => {
    fetchChallenges();
    fetchStudentChallenge();
  }, []);

  const breadcrumbs = {
    title: 'Aprendizado Dinâmico',
    nav: [
      {
        route: '/desafio-da-semana/resultados',
        name: 'Desafio da Semana',
        isActive: false
      },
      {
        route: `/desafio-da-semana/resultados/${challengeId}`,
        name: challenges[0]?.challenge?.title,
        isActive: false
      },
      {
        route: uri,
        name: studentChallenge?.user?.name,
        isActive: true
      }
    ]
  };

  const breadcrumbsInRank = {
    title: 'Aprendizado Dinâmico',
    nav: [
      {
        route: '/desafio-da-semana/resultados',
        name: 'Desafio da Semana',
        isActive: true
      }
    ]
  };

  const hasChallenges = orderedChallenges?.length > 0;

  const isStudent = profile === 'estudante';
  const isParticipantRanking = selectedTab == 'ranking-por-participante';
  const hasChallengeContext = challengeId && challengeId !== 'challenge';
  const challengeContext = challenges.find(item => String(item.challenge?.id) === String(challengeId))?.challenge;

  const playfulBreadcrumbs = {
    title: 'Aprendizado Dinâmico - Desafio da Semana',
    nav: hasChallengeContext
      ? [
          {
            route: '/desafio-da-semana/resultados',
            name: 'Resultados',
            isActive: false
          },
          {
            route: `/desafio-da-semana/resultados/${challengeId}`,
            name: challengeContext?.title,
            isActive: false
          },
          {
            route: uri,
            name: studentChallenge?.user?.name,
            isActive: true
          }
        ]
      : [
          {
            route: '/desafio-da-semana/ranking-por-participante',
            name: 'Ranking por participante',
            isActive: false
          },
          {
            route: uri,
            name: studentChallenge?.user?.name,
            isActive: true
          }
        ]
  };

  return (
    <DashboardContent
      variant="plain"
      breadcrumbs={isPlayfulTeacherTheme ? playfulBreadcrumbs : isParticipantRanking ? breadcrumbsInRank : breadcrumbs}
    >
      {isPlayfulTeacherTheme && (
        <TiaBanner
          className="mb-6 sm:mt-24"
          image={JOINHA}
          imageAlt="Personagem do professor"
          imageHeight={215}
          imageOffset={200}
          bubbleCorner="tl"
          bubbleMaxWidth={600}
          text={`Aqui você acompanha de perto o desempenho de ${studentChallenge?.user?.name ?? 'cada participante'}! Veja os acertos, os desafios realizados e os destaques por matéria. 📊`}
        />
      )}

      {!isPlayfulTeacherTheme && isParticipantRanking && <ChallengeTab selectedTab={selectedTab} />}

      {!isPlayfulTeacherTheme && isParticipantRanking && (
        <div className="filter-bar">
          <div className="filter-bar__inner">
            <button
              className="filter-bar__back"
              onClick={() => navigate(-1)}
            >
              <span className="card__icon-wrapper card__icon-wrapper--sm">
                <FiChevronLeft className="card__icon" />
              </span>
              <span className="filter-bar__back-container">
                <span className="filter-bar__title">{studentChallenge?.user?.name} </span>
              </span>
            </button>
          </div>
        </div>
      )}

      {isPlayfulTeacherTheme ? (
        <ParticipantSummary
          showTrophy={state?.position}
          studentChallenge={studentChallenge}
          isStudent={isStudent}
          selectedTab={selectedTab}
        />
      ) : (
        <ChallengeResultBanner
          showTrophy={state?.position}
          studentchallenge={studentChallenge}
          isStudent={isStudent}
          selectedTab={selectedTab}
        />
      )}

      {isStudent && (
        <div className="filter-bar filter-bar--borderless">
          <div className="filter-bar__inner">
            <FilterSelectNative
              optionBlank={''}
              label={'Tema'}
              blankLabel={'Selecione um tema'}
              value={filters.values.theme}
              onChange={e => filters.setFieldValue('theme', e.target.value)}
              // options={themes}
            />
          </div>
        </div>
      )}

      <ChallengeHeader
        playful={isPlayfulTeacherTheme}
        setParam={setParam}
        setOrderByAccuracy={setOrderByAccuracy}
        setOrderByPeriod={setOrderByPeriod}
        orderByAccuracy={orderByAccuracy}
        orderByPeriod={orderByPeriod}
        selectedTab={selectedTab}
        isStudent={isStudent}
      />

      {loading ? (
        <Loader />
      ) : hasChallenges ? (
        <div className="accordion">
          {orderedChallenges?.map(item => (
            <AccordionItem
              playful={isPlayfulTeacherTheme}
              taxonomies={item?.['accuracy-by-taxonomy']}
              studentAccuracy={item?.['student-accuracy']}
              challenge={item.challenge}
              isStudent={isStudent}
              selectedTab={selectedTab}
              key={item.id}
            />
          ))}
        </div>
      ) : (
        <EmptyState type="data" />
      )}
    </DashboardContent>
  );
}
